import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, SubmitHandler } from 'react-hook-form';
import styled from 'styled-components';
import { Save, ArrowLeft, Eye, EyeOff, RefreshCw, Check, Square, Settings, MapPin, Plus, Edit, Trash2 } from 'lucide-react';
import { CreateDealerRequest, UpdateDealerRequest, Dealer, ServiceType, DealerLocation, CreateLocationRequest } from '../types';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 24px;
  
  h1 {
    margin: 0;
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
  border-radius: 6px;
  text-decoration: none;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    border-color: #9ca3af;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const FormCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  &:not(:last-child) {
    padding-bottom: 24px;
    border-bottom: 1px solid #e5e7eb;
  }
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
`;

const Input = styled.input<{ hasError?: boolean }>`
  padding: 12px 16px;
  border: 2px solid ${props => props.hasError ? '#ef4444' : '#d1d5db'};
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ef4444' : '#ed2024'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(102, 126, 234, 0.1)'};
  }
  
  &.password {
    padding-right: 48px;
  }
`;

const PasswordContainer = styled.div`
  position: relative;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: #374151;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ColorInput = styled.input`
  width: 60px;
  height: 40px;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  cursor: pointer;
  
  &::-webkit-color-swatch-wrapper {
    padding: 0;
  }
  
  &::-webkit-color-swatch {
    border: none;
    border-radius: 6px;
  }
`;

const TagsInput = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px;
  border: 2px solid #d1d5db;
  border-radius: 8px;
  min-height: 44px;
  
  &:focus-within {
    border-color: #ed2024;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Tag = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: #e5e7eb;
  border-radius: 4px;
  font-size: 0.875rem;
  
  button {
    background: none;
    border: none;
    color: #6b7280;
    cursor: pointer;
    padding: 0;
    font-size: 14px;
    
    &:hover {
      color: #374151;
    }
  }
`;

const TagInput = styled.input`
  flex: 1;
  min-width: 120px;
  border: none;
  outline: none;
  padding: 4px;
  font-size: 0.875rem;
`;

const ErrorMessage = styled.span`
  color: #ef4444;
  font-size: 0.875rem;
`;

const HelpText = styled.span`
  color: #6b7280;
  font-size: 0.75rem;
`;

const ServiceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
`;

const ServiceItem = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px;
  border: 2px solid ${props => props.selected ? '#ed2024' : '#e5e7eb'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #ed2024;
    background: ${props => props.selected ? '#f8f9ff' : 'white'};
  }
`;

const ServiceCheckbox = styled.div<{ checked: boolean }>`
  width: 20px;
  height: 20px;
  border: 2px solid ${props => props.checked ? '#ed2024' : '#d1d5db'};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
  background: ${props => props.checked ? '#ed2024' : 'white'};
  color: white;
  transition: all 0.2s;

  svg {
    width: 14px;
    height: 14px;
  }
`;

const ServiceInfo = styled.div`
  flex: 1;
`;

const ServiceName = styled.h4`
  margin: 0 0 4px 0;
  font-size: 1rem;
  color: #1f2937;
`;

const ServiceDescription = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.4;
`;

const ServiceMeta = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;
  font-size: 0.875rem;
  color: #6b7280;
`;

const ServiceActions = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
`;

const GlobalStyle = styled.div`
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .spin {
    animation: spin 1s linear infinite;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border: ${props => props.variant === 'primary' ? 'none' : '1px solid #d1d5db'};
  background: ${props => props.variant === 'primary' ? 'linear-gradient(135deg, #ed2024 0%, #a01519 100%)' : 'white'};
  color: ${props => props.variant === 'primary' ? 'white' : '#374151'};
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    ${props => props.variant === 'primary' ? `
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    ` : `
      background: #f3f4f6;
      border-color: #9ca3af;
    `}
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Toggle = styled.div<{ enabled: boolean }>`
  width: 48px;
  height: 24px;
  background: ${props => props.enabled ? '#ed2024' : '#d1d5db'};
  border-radius: 12px;
  position: relative;
  cursor: pointer;
  transition: background 0.2s;
  
  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.enabled ? '26px' : '2px'};
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    transition: left 0.2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
`;

const ToggleLabel = styled.label`
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  user-select: none;
`;

const Select = styled.select<{ hasError?: boolean }>`
  width: 100%;
  padding: 10px 12px;
  border: 2px solid ${props => props.hasError ? '#ef4444' : '#e5e7eb'};
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#ef4444' : '#ed2024'};
    box-shadow: 0 0 0 3px ${props => props.hasError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(102, 126, 234, 0.1)'};
  }
`;

const LocationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const LocationCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  background: #f9fafb;
  
  .location-header {
    display: flex;
    align-items: center;
    justify-content: between;
    margin-bottom: 12px;
    
    .location-name {
      font-weight: 600;
      color: #1f2937;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    
    .location-actions {
      margin-left: auto;
      display: flex;
      gap: 8px;
    }
  }
  
  .location-details {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    font-size: 0.875rem;
    color: #6b7280;
    
    .detail-item {
      display: flex;
      flex-direction: column;
      
      .label {
        font-weight: 500;
        color: #374151;
        margin-bottom: 2px;
      }
    }
  }
`;

const AddLocationButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border: 2px dashed #d1d5db;
  background: white;
  color: #6b7280;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
  
  &:hover {
    border-color: #ed2024;
    color: #ed2024;
    background: #f8f9ff;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  
  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    
    h3 {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
      color: #1f2937;
    }
    
    button {
      background: none;
      border: none;
      font-size: 1.5rem;
      color: #6b7280;
      cursor: pointer;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      &:hover {
        color: #374151;
      }
    }
  }
`;

const DealerForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;
  
  const [isLoading, setIsLoading] = useState(false);
  const [showAccessKey, setShowAccessKey] = useState(false);
  const [allowedDomains, setAllowedDomains] = useState<string[]>([]);
  const [domainInput, setDomainInput] = useState('');
  const [availableServices, setAvailableServices] = useState<ServiceType[]>([]);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const [useThirdPartyIntegration, setUseThirdPartyIntegration] = useState(false);
  const [thirdPartyProvider, setThirdPartyProvider] = useState<'tireworkshd' | ''>('');
  const [locations, setLocations] = useState<DealerLocation[]>([]);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [isEditLocationModalOpen, setIsEditLocationModalOpen] = useState(false);
  const [editingLocation, setEditingLocation] = useState<DealerLocation | null>(null);
  const [newLocation, setNewLocation] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: ''
  });
  const [editLocation, setEditLocation] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: ''
  });

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<CreateDealerRequest>();

  useEffect(() => {
    if (isEditing && id) {
      loadDealer(id);
      loadLocations(id);
    }
  }, [id, isEditing]);

  const loadDealer = async (dealerId: string) => {
    try {
      setIsLoading(true);
      const response = await apiService.getDealer(dealerId);
      if (response.success && response.data) {
        const dealer = response.data;
        
        // Set form values
        Object.keys(dealer).forEach((key) => {
          if (key === 'allowedDomains' && dealer.config?.allowedDomains) {
            setAllowedDomains(dealer.config.allowedDomains);
          } else if (key !== 'id' && key !== 'createdAt' && key !== 'updatedAt' && key !== 'config' && key !== 'embedToken') {
            setValue(key as keyof CreateDealerRequest, (dealer as any)[key]);
          }
        });

        // Set config values
        if (dealer.config) {
          setValue('primaryColor', dealer.config.primaryColor);
          setValue('fontFamily', dealer.config.fontFamily);
        }
        // allowedServices lives at the top level of the dealer object (not inside config)
        if (dealer.allowedServices && dealer.allowedServices.length > 0) {
          setSelectedServices(dealer.allowedServices);
        }

        // Set third-party integration values
        setUseThirdPartyIntegration(dealer.useThirdPartyIntegration || false);
        setThirdPartyProvider(dealer.thirdPartyProvider || '');

        // Set API credentials values
        if (dealer.apiCredentials) {
          setValue('tireworksApiEndpoint', dealer.apiCredentials.apiEndpoint || '');
          setValue('tireworksLocationId', dealer.apiCredentials.locationId || '');
          setValue('tireworksApplicationUID', dealer.apiCredentials.applicationUID || '');
        }
      } else {
        toast.error('Failed to load dealer');
        navigate('/dealers');
      }
    } catch (error) {
      console.error('Error loading dealer:', error);
      toast.error('Failed to load dealer');
      navigate('/dealers');
    } finally {
      setIsLoading(false);
    }
  };

  const loadLocations = async (dealerId: string) => {
    try {
      const response = await apiService.getDealerLocations(dealerId);
      if (response.success && response.data) {
        setLocations(response.data);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
      // Don't show error toast for locations as it's not critical for dealer editing
    }
  };

  const handleAddLocation = async () => {
    if (!newLocation.name.trim() || !id) return;

    try {
      const locationData: CreateLocationRequest = {
        dealerId: id,
        name: newLocation.name.trim(),
        address: newLocation.address.trim() || undefined,
        city: newLocation.city.trim() || undefined,
        state: newLocation.state.trim() || undefined,
        zipCode: newLocation.zipCode.trim() || undefined,
        phone: newLocation.phone.trim() || undefined,
        email: newLocation.email.trim() || undefined
      };

      const response = await apiService.createLocation(id, locationData);
      if (response.success && response.data) {
        setLocations(prev => [...prev, response.data!]);
        setNewLocation({
          name: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
          phone: '',
          email: ''
        });
        setIsLocationModalOpen(false);
        toast.success('Location added successfully');
      } else {
        toast.error('Failed to add location');
      }
    } catch (error) {
      console.error('Error adding location:', error);
      toast.error('Failed to add location');
    }
  };

  const updateNewLocationField = (field: string, value: string) => {
    setNewLocation(prev => ({ ...prev, [field]: value }));
  };

  const updateEditLocationField = (field: string, value: string) => {
    setEditLocation(prev => ({ ...prev, [field]: value }));
  };

  const handleEditLocation = (location: DealerLocation) => {
    setEditingLocation(location);
    setEditLocation({
      name: location.name,
      address: location.address || '',
      city: location.city || '',
      state: location.state || '',
      zipCode: location.zipCode || '',
      phone: location.phone || '',
      email: location.email || ''
    });
    setIsEditLocationModalOpen(true);
  };

  const handleUpdateLocation = async () => {
    if (!editLocation.name.trim() || !editingLocation) return;

    try {
      const locationData = {
        id: editingLocation.id,
        name: editLocation.name.trim(),
        address: editLocation.address.trim() || undefined,
        city: editLocation.city.trim() || undefined,
        state: editLocation.state.trim() || undefined,
        zipCode: editLocation.zipCode.trim() || undefined,
        phone: editLocation.phone.trim() || undefined,
        email: editLocation.email.trim() || undefined
      };

      const response = await apiService.updateLocation(editingLocation.id, locationData);
      if (response.success && response.data) {
        setLocations(prev => prev.map(loc => 
          loc.id === editingLocation.id ? response.data! : loc
        ));
        setIsEditLocationModalOpen(false);
        setEditingLocation(null);
        toast.success('Location updated successfully');
      } else {
        toast.error('Failed to update location');
      }
    } catch (error) {
      console.error('Error updating location:', error);
      toast.error('Failed to update location');
    }
  };

  const handleDeleteLocation = async (location: DealerLocation) => {
    if (!window.confirm(`Are you sure you want to delete "${location.name}"? This action cannot be undone and will also delete all associated hours and service assignments.`)) {
      return;
    }

    try {
      const response = await apiService.deleteLocation(location.id);
      if (response.success) {
        setLocations(prev => prev.filter(loc => loc.id !== location.id));
        toast.success('Location deleted successfully');
      } else {
        toast.error('Failed to delete location');
      }
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error('Failed to delete location');
    }
  };

  const onSubmit: SubmitHandler<CreateDealerRequest> = async (data) => {
    try {
      setIsLoading(true);
      
      const { tireworksApiEndpoint, tireworksLocationId, tireworksApplicationUID, ...rest } = data;

      // Build a base payload with third-party integration and TireWorks fields
      const dealerPayload = {
        ...rest,
        useThirdPartyIntegration,
        thirdPartyProvider: useThirdPartyIntegration ? thirdPartyProvider : null,
        // Only include TireWorks credentials if third-party integration is enabled and TireWorks is selected
        tireworksApiEndpoint: (useThirdPartyIntegration && thirdPartyProvider === 'tireworkshd') ? tireworksApiEndpoint : undefined,
        tireworksLocationId: (useThirdPartyIntegration && thirdPartyProvider === 'tireworkshd') ? tireworksLocationId : undefined,
        tireworksApplicationUID: (useThirdPartyIntegration && thirdPartyProvider === 'tireworkshd') ? tireworksApplicationUID : undefined,
        allowedDomains,
        allowedServices: (useThirdPartyIntegration && thirdPartyProvider === 'tireworkshd') ? selectedServices : []
      };

      let response;
      if (isEditing && id) {
        response = await apiService.updateDealer(id, dealerPayload as UpdateDealerRequest);
      } else {
        response = await apiService.createDealer(dealerPayload as CreateDealerRequest);
      }

      if (response.success) {
        toast.success(isEditing ? 'Dealer updated successfully' : 'Dealer created successfully');
        navigate('/dealers');
      } else {
        toast.error(response.error || 'Failed to save dealer');
      }
    } catch (error) {
      console.error('Error saving dealer:', error);
      toast.error('Failed to save dealer');
    } finally {
      setIsLoading(false);
    }
  };

  const addDomain = () => {
    if (domainInput.trim() && !allowedDomains.includes(domainInput.trim())) {
      setAllowedDomains([...allowedDomains, domainInput.trim()]);
      setDomainInput('');
    }
  };

  const removeDomain = (domain: string) => {
    setAllowedDomains(allowedDomains.filter(d => d !== domain));
  };

  const handleDomainKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addDomain();
    }
  };

  const fetchAvailableServices = async () => {
    const tireworksApiEndpoint = watch('tireworksApiEndpoint');
    const tireworksLocationId = watch('tireworksLocationId');
    const tireworksApplicationUID = watch('tireworksApplicationUID');

    if (!tireworksApiEndpoint || !tireworksLocationId || !tireworksApplicationUID) {
      toast.error('Please fill in all TireWorks HD API credentials first');
      return;
    }

    try {
      setIsLoadingServices(true);
      const response = await apiService.getTireWorksServices({
        apiEndpoint: tireworksApiEndpoint,
        locationId: tireworksLocationId,
        applicationUID: tireworksApplicationUID
      });

      if (response.success && response.data) {
        setAvailableServices(response.data);
        toast.success('Services loaded successfully');
      } else {
        toast.error(response.error || 'Failed to load services');
      }
    } catch (error) {
      console.error('Error loading services:', error);
      toast.error('Failed to load services');
    } finally {
      setIsLoadingServices(false);
    }
  };

  const toggleService = (serviceId: string) => {
    setSelectedServices(prev => {
      if (prev.includes(serviceId)) {
        return prev.filter(id => id !== serviceId);
      } else {
        return [...prev, serviceId];
      }
    });
  };

  const handleCheckAll = () => {
    setSelectedServices(availableServices.map(service => service.id));
  };

  const handleUncheckAll = () => {
    setSelectedServices([]);
  };

  return (
    <GlobalStyle>
    <PageContainer>
      <PageHeader>
        <BackButton onClick={() => navigate('/dealers')}>
          <ArrowLeft />
          Back to Dealers
        </BackButton>
        <h1>{isEditing ? 'Edit Dealer' : 'Add New Dealer'}</h1>
      </PageHeader>

      <FormCard>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Section>
            <SectionTitle>Basic Information</SectionTitle>
            
            <FormRow>
              <FormGroup>
                <Label htmlFor="name">Dealer Name *</Label>
                <Input
                  id="name"
                  hasError={!!errors.name}
                  {...register('name', { required: 'Dealer name is required' })}
                />
                {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  hasError={!!errors.email}
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: 'Please enter a valid email address'
                    }
                  })}
                />
                {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label htmlFor="phone">Phone Number *</Label>
                <Input
                  id="phone"
                  hasError={!!errors.phone}
                  {...register('phone', { required: 'Phone number is required' })}
                />
                {errors.phone && <ErrorMessage>{errors.phone.message}</ErrorMessage>}
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="website">Website</Label>
                <Input
                  id="website"
                  type="url"
                  placeholder="https://example.com"
                  hasError={!!errors.website}
                  {...register('website')}
                />
                {errors.website && <ErrorMessage>{errors.website.message}</ErrorMessage>}
              </FormGroup>
            </FormRow>
          </Section>

          <Section>
            <SectionTitle>Address</SectionTitle>
            
            <FormGroup>
              <Label htmlFor="address">Street Address *</Label>
              <Input
                id="address"
                hasError={!!errors.address}
                {...register('address', { required: 'Address is required' })}
              />
              {errors.address && <ErrorMessage>{errors.address.message}</ErrorMessage>}
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label htmlFor="city">City *</Label>
                <Input
                  id="city"
                  hasError={!!errors.city}
                  {...register('city', { required: 'City is required' })}
                />
                {errors.city && <ErrorMessage>{errors.city.message}</ErrorMessage>}
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="state">State *</Label>
                <Input
                  id="state"
                  hasError={!!errors.state}
                  {...register('state', { required: 'State is required' })}
                />
                {errors.state && <ErrorMessage>{errors.state.message}</ErrorMessage>}
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="zipCode">ZIP Code *</Label>
                <Input
                  id="zipCode"
                  hasError={!!errors.zipCode}
                  {...register('zipCode', { required: 'ZIP code is required' })}
                />
                {errors.zipCode && <ErrorMessage>{errors.zipCode.message}</ErrorMessage>}
              </FormGroup>
            </FormRow>
          </Section>

          <Section>
            <SectionTitle>
              <Settings style={{ marginRight: '8px' }} />
              Integration Settings
            </SectionTitle>
            
            <FormGroup>
              <ToggleContainer>
                <Toggle 
                  enabled={useThirdPartyIntegration}
                  onClick={() => {
                    setUseThirdPartyIntegration(!useThirdPartyIntegration);
                    if (!useThirdPartyIntegration) {
                      setThirdPartyProvider('');
                    }
                  }}
                />
                <ToggleLabel onClick={() => setUseThirdPartyIntegration(!useThirdPartyIntegration)}>
                  Enable Third-Party Integration
                </ToggleLabel>
              </ToggleContainer>
              <HelpText>
                Enable this to connect with external appointment management systems. 
                When disabled, appointments will be managed using this product's built-in calendar.
              </HelpText>
            </FormGroup>

            {useThirdPartyIntegration && (
              <FormGroup>
                <Label htmlFor="thirdPartyProvider">Integration Provider *</Label>
                <Select
                  id="thirdPartyProvider"
                  value={thirdPartyProvider}
                  onChange={(e) => setThirdPartyProvider(e.target.value as 'tireworkshd' | '')}
                  hasError={useThirdPartyIntegration && !thirdPartyProvider}
                >
                  <option value="">Select a provider...</option>
                  <option value="tireworkshd">TireWorks HD</option>
                </Select>
                {useThirdPartyIntegration && !thirdPartyProvider && (
                  <ErrorMessage>Please select an integration provider</ErrorMessage>
                )}
                <HelpText>Choose which third-party system to integrate with for appointment management</HelpText>
              </FormGroup>
            )}
          </Section>

          {useThirdPartyIntegration && thirdPartyProvider === 'tireworkshd' && (
            <Section>
              <SectionTitle>TireWorks HD API Configuration</SectionTitle>
            
            <FormGroup>
              <Label htmlFor="tireworksApiEndpoint">API Endpoint *</Label>
                <Input
                id="tireworksApiEndpoint"
                type="url"
                placeholder="https://api.tireworkshd.com/webservices"
                hasError={!!errors.tireworksApiEndpoint}
                  autoComplete="off"
                {...register('tireworksApiEndpoint', {
                  required: 'API endpoint is required',
                  pattern: {
                    value: /^https?:\/\/.+/,
                    message: 'Please enter a valid URL'
                  }
                })}
              />
              {errors.tireworksApiEndpoint && <ErrorMessage>{errors.tireworksApiEndpoint.message}</ErrorMessage>}
              <HelpText>The SOAP endpoint URL for TireWorks HD web services</HelpText>
            </FormGroup>

            <FormRow>
              <FormGroup>
                <Label htmlFor="tireworksLocationId">Location ID *</Label>
                <Input
                  id="tireworksLocationId"
                  hasError={!!errors.tireworksLocationId}
                  placeholder="101"
                  inputMode="numeric"
                  autoComplete="off"
                  {...register('tireworksLocationId', { required: 'Location ID is required' })}
                />
                {errors.tireworksLocationId && <ErrorMessage>{errors.tireworksLocationId.message}</ErrorMessage>}
                <HelpText>Your TireWorks HD location identifier (numeric)</HelpText>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="tireworksApplicationUID">Application UID *</Label>
                <PasswordContainer>
                  <Input
                    id="tireworksApplicationUID"
                    type={showAccessKey ? 'text' : 'password'}
                    className="password"
                    placeholder="90CE6794-3F04-4D63-82F5-CA7AEBFFE38D"
                    hasError={!!errors.tireworksApplicationUID}
                    autoComplete="new-password"
                    {...register('tireworksApplicationUID', { 
                      required: 'Application UID is required',
                      pattern: {
                        value: /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
                        message: 'Please enter a valid GUID format'
                      }
                    })}
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowAccessKey(!showAccessKey)}
                  >
                    {showAccessKey ? <EyeOff /> : <Eye />}
                  </PasswordToggle>
                </PasswordContainer>
                {errors.tireworksApplicationUID && <ErrorMessage>{errors.tireworksApplicationUID.message}</ErrorMessage>}
                <HelpText>GUID provided by TireWorks HD for API authentication</HelpText>
              </FormGroup>
            </FormRow>
          </Section>
          )}

          {useThirdPartyIntegration && thirdPartyProvider === 'tireworkshd' && (
            <Section>
              <SectionTitle>Available Services</SectionTitle>
            <div>
              <Button 
                type="button" 
                onClick={fetchAvailableServices}
                disabled={isLoadingServices}
              >
                <RefreshCw className={isLoadingServices ? 'spin' : ''} />
                {isLoadingServices ? 'Loading Services...' : 'Get Available Services'}
              </Button>
            </div>

            {availableServices.length > 0 && (
              <>
                <ServiceActions>
                  <Button type="button" variant="secondary" onClick={handleCheckAll}>
                    <Check /> Check All
                  </Button>
                  <Button type="button" variant="secondary" onClick={handleUncheckAll}>
                    <Square /> Uncheck All
                  </Button>
                </ServiceActions>

                <ServiceList>
                  {availableServices.map(service => (
                    <ServiceItem
                      key={service.id}
                      selected={selectedServices.includes(service.id)}
                      onClick={() => toggleService(service.id)}
                    >
                      <ServiceCheckbox checked={selectedServices.includes(service.id)}>
                        {selectedServices.includes(service.id) && <Check />}
                      </ServiceCheckbox>
                      <ServiceInfo>
                        <ServiceName>{service.name}</ServiceName>
                        <ServiceDescription>{service.description}</ServiceDescription>
                        <ServiceMeta>
                          <span>Duration: {service.estimatedDuration} min</span>
                          {service.price && <span>Price: ${service.price}</span>}
                        </ServiceMeta>
                      </ServiceInfo>
                    </ServiceItem>
                  ))}
                </ServiceList>
              </>
            )}
          </Section>
          )}

          <Section>
            <SectionTitle>Customization</SectionTitle>
            
            <FormRow>
              <FormGroup>
                <Label htmlFor="primaryColor">Primary Color</Label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <ColorInput
                    id="primaryColor"
                    type="color"
                    {...register('primaryColor')}
                  />
                  <Input
                    type="text"
                    placeholder="#ed2024"
                    {...register('primaryColor')}
                    style={{ flex: 1 }}
                  />
                </div>
                <HelpText>Color used for buttons and accents in the scheduler</HelpText>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="fontFamily">Font Family</Label>
                <Input
                  id="fontFamily"
                  placeholder="system-ui, sans-serif"
                  {...register('fontFamily')}
                />
                <HelpText>CSS font family for the scheduler interface</HelpText>
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>Allowed Domains</Label>
              <TagsInput>
                {allowedDomains.map((domain) => (
                  <Tag key={domain}>
                    {domain}
                    <button type="button" onClick={() => removeDomain(domain)}>×</button>
                  </Tag>
                ))}
                <TagInput
                  type="text"
                  placeholder="example.com"
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                  onKeyPress={handleDomainKeyPress}
                  onBlur={addDomain}
                />
              </TagsInput>
              <HelpText>Domains where this scheduler can be embedded (leave empty to allow all domains)</HelpText>
            </FormGroup>
          </Section>

          {isEditing && (
            <Section>
              <SectionTitle>
                <MapPin style={{ marginRight: '8px' }} />
                Locations
              </SectionTitle>
              
              <LocationsList>
                {locations.map((location) => (
                  <LocationCard key={location.id}>
                    <div className="location-header">
                      <div className="location-name">
                        <MapPin size={16} />
                        {location.name}
                      </div>
                      <div className="location-actions">
                        <Button type="button" variant="secondary" onClick={() => handleEditLocation(location)}>
                          <Edit size={14} />
                          Edit
                        </Button>
                        <Button type="button" variant="secondary" onClick={() => {
                          // Store dealer ID for location navigation
                          if (id) sessionStorage.setItem('currentDealerId', id);
                          navigate(`/locations/${location.id}/hours`);
                        }}>
                          Manage Hours
                        </Button>
                        <Button type="button" variant="secondary" onClick={() => {
                          // Store dealer ID for location navigation
                          if (id) sessionStorage.setItem('currentDealerId', id);
                          navigate(`/locations/${location.id}/bays`);
                        }}>
                          Manage Bays
                        </Button>
                        <Button type="button" variant="secondary" onClick={() => {
                          // Store dealer ID for location navigation
                          if (id) sessionStorage.setItem('currentDealerId', id);
                          navigate(`/locations/${location.id}/services`);
                        }}>
                          Assign Services
                        </Button>
                        <Button type="button" variant="secondary" onClick={() => handleDeleteLocation(location)} style={{ color: '#dc2626' }}>
                          <Trash2 size={14} />
                          Delete
                        </Button>
                      </div>
                    </div>
                    <div className="location-details">
                      {location.address && (
                        <div className="detail-item">
                          <div className="label">Address</div>
                          <div>{location.address}</div>
                        </div>
                      )}
                      {(location.city || location.state || location.zipCode) && (
                        <div className="detail-item">
                          <div className="label">City, State ZIP</div>
                          <div>{location.city}, {location.state} {location.zipCode}</div>
                        </div>
                      )}
                      {location.phone && (
                        <div className="detail-item">
                          <div className="label">Phone</div>
                          <div>{location.phone}</div>
                        </div>
                      )}
                      {location.email && (
                        <div className="detail-item">
                          <div className="label">Email</div>
                          <div>{location.email}</div>
                        </div>
                      )}
                    </div>
                  </LocationCard>
                ))}
                
                <AddLocationButton type="button" onClick={() => setIsLocationModalOpen(true)}>
                  <Plus />
                  Add Location
                </AddLocationButton>
              </LocationsList>
              
              <HelpText>
                Locations allow you to manage different physical sites for this dealer. Each location can have its own hours of operation and service assignments.
              </HelpText>
            </Section>
          )}

          <ButtonGroup>
            <Button type="button" onClick={() => navigate('/dealers')}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isLoading}>
              {isLoading ? (
                <>
                  <div style={{ 
                    width: '20px', 
                    height: '20px', 
                    border: '2px solid rgba(255,255,255,0.3)', 
                    borderTop: '2px solid white', 
                    borderRadius: '50%', 
                    animation: 'spin 1s linear infinite' 
                  }} />
                  Saving...
                </>
              ) : (
                <>
                  <Save />
                  {isEditing ? 'Update Dealer' : 'Create Dealer'}
                </>
              )}
            </Button>
          </ButtonGroup>
        </Form>
      </FormCard>

      {/* Add Location Modal */}
      <Modal isOpen={isLocationModalOpen}>
        <ModalContent>
          <div className="modal-header">
            <h3>Add New Location</h3>
            <button onClick={() => setIsLocationModalOpen(false)}>×</button>
          </div>
          
          <FormGroup>
            <Label htmlFor="locationName">Location Name *</Label>
            <Input
              id="locationName"
              value={newLocation.name}
              onChange={(e) => updateNewLocationField('name', e.target.value)}
              placeholder="e.g., Main Store, Downtown Location"
              hasError={false}
            />
            <HelpText>Enter a descriptive name for this location</HelpText>
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="locationAddress">Street Address</Label>
            <Input
              id="locationAddress"
              value={newLocation.address}
              onChange={(e) => updateNewLocationField('address', e.target.value)}
              placeholder="123 Main Street"
              hasError={false}
            />
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Label htmlFor="locationCity">City</Label>
              <Input
                id="locationCity"
                value={newLocation.city}
                onChange={(e) => updateNewLocationField('city', e.target.value)}
                placeholder="New York"
                hasError={false}
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="locationState">State</Label>
              <Input
                id="locationState"
                value={newLocation.state}
                onChange={(e) => updateNewLocationField('state', e.target.value)}
                placeholder="NY"
                hasError={false}
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label htmlFor="locationZipCode">ZIP Code</Label>
              <Input
                id="locationZipCode"
                value={newLocation.zipCode}
                onChange={(e) => updateNewLocationField('zipCode', e.target.value)}
                placeholder="10001"
                hasError={false}
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="locationPhone">Phone</Label>
              <Input
                id="locationPhone"
                value={newLocation.phone}
                onChange={(e) => updateNewLocationField('phone', e.target.value)}
                placeholder="(555) 123-4567"
                hasError={false}
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label htmlFor="locationEmail">Email</Label>
            <Input
              id="locationEmail"
              type="email"
              value={newLocation.email}
              onChange={(e) => updateNewLocationField('email', e.target.value)}
              placeholder="location@dealership.com"
              hasError={false}
            />
          </FormGroup>
          
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
            <Button type="button" onClick={() => setIsLocationModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              type="button" 
              variant="primary" 
              onClick={handleAddLocation}
              disabled={!newLocation.name.trim()}
            >
              Add Location
            </Button>
          </div>
        </ModalContent>
      </Modal>

      {/* Edit Location Modal */}
      <Modal isOpen={isEditLocationModalOpen}>
        <ModalContent>
          <div className="modal-header">
            <h3>Edit Location</h3>
            <button onClick={() => setIsEditLocationModalOpen(false)}>×</button>
          </div>
          
          <FormGroup>
            <Label htmlFor="editLocationName">Location Name *</Label>
            <Input
              id="editLocationName"
              value={editLocation.name}
              onChange={(e) => updateEditLocationField('name', e.target.value)}
              placeholder="e.g., Main Store, Downtown Location"
              hasError={false}
            />
            <HelpText>Enter a descriptive name for this location</HelpText>
          </FormGroup>
          
          <FormGroup>
            <Label htmlFor="editLocationAddress">Street Address</Label>
            <Input
              id="editLocationAddress"
              value={editLocation.address}
              onChange={(e) => updateEditLocationField('address', e.target.value)}
              placeholder="123 Main Street"
              hasError={false}
            />
          </FormGroup>

          <FormRow>
            <FormGroup>
              <Label htmlFor="editLocationCity">City</Label>
              <Input
                id="editLocationCity"
                value={editLocation.city}
                onChange={(e) => updateEditLocationField('city', e.target.value)}
                placeholder="New York"
                hasError={false}
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="editLocationState">State</Label>
              <Input
                id="editLocationState"
                value={editLocation.state}
                onChange={(e) => updateEditLocationField('state', e.target.value)}
                placeholder="NY"
                hasError={false}
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label htmlFor="editLocationZipCode">ZIP Code</Label>
              <Input
                id="editLocationZipCode"
                value={editLocation.zipCode}
                onChange={(e) => updateEditLocationField('zipCode', e.target.value)}
                placeholder="10001"
                hasError={false}
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="editLocationPhone">Phone</Label>
              <Input
                id="editLocationPhone"
                value={editLocation.phone}
                onChange={(e) => updateEditLocationField('phone', e.target.value)}
                placeholder="(555) 123-4567"
                hasError={false}
              />
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label htmlFor="editLocationEmail">Email</Label>
            <Input
              id="editLocationEmail"
              type="email"
              value={editLocation.email}
              onChange={(e) => updateEditLocationField('email', e.target.value)}
              placeholder="location@dealership.com"
              hasError={false}
            />
          </FormGroup>
          
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
            <Button type="button" onClick={() => setIsEditLocationModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              type="button" 
              variant="primary" 
              onClick={handleUpdateLocation}
              disabled={!editLocation.name.trim()}
            >
              Update Location
            </Button>
          </div>
        </ModalContent>
      </Modal>
    </PageContainer>
    </GlobalStyle>
  );
};

export default DealerForm;